import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageWrapper } from '../../components';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  getLoading,
  getSelectedAccount,
  getForecastDateLocal,
  selectRevenuePageFilters,
  getUniqueFilterLists,
} from '../../redux/quarterlyRevenue/quarterlyRevenue.selectors';
import { DateTime } from 'luxon';
import {
  Box,
  Button,
  Stack,
  Typography,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Paper,
  Tooltip,
  SxProps,
  TableCellProps,
  Theme,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCommentIcon from '@mui/icons-material/AddComment';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AnyAction } from '@reduxjs/toolkit';
import ForecastModal from './ForecastModal';
import {
  getQuarterlyRevenueAccount,
  setForecastDateLocal,
  setForecastDate,
  cleanForecastDate,
  setRevenuePageFilters,
  setSortBy,
  resetRevenuePageFilters,
} from '../../redux/quarterlyRevenue';
import {
  getSortBy,
  getSortOrder,
} from '../../redux/quarterlyRevenue/quarterlyRevenue.selectors';
import { SortAccountsBy } from '../../redux/quarterlyRevenue/quarterlyRevenue.types';
import { SortOrder } from '../../types';
import {
  GrowthSegmentationLabels,
  Mode,
  QuarterlyRevenueAccountSummary,
  QuarterlyRevenueFilter,
} from '../../types/quarterlyRevenue';
import Papaparse from 'papaparse';
import GridOnIcon from '@mui/icons-material/GridOn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { downloadCsv } from '../reports/Reports.utils';
import {
  agileEngineRevenueLabels,
  clientGrowthLabels,
  RevenueStatusChip,
} from '../../components/revenueStatusChip/RevenueStatusChip';
import QuarterPicker from '../../components/datePicker/QuarterPicker';
import { parseQuarterParam } from '../../utils/date';

import EditIcon from '@mui/icons-material/Edit';
import { SCREEN_WIDTH } from '../../components/finKitAvatar/breakpoints';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { throttle } from 'lodash';

interface DateFilterActions {
  func: (value: string | null) => AnyAction;
  setGlobalFunc: () => AnyAction;
  clean: () => AnyAction;
  type: 'dispatch';
}

export const QuarterlyRevenueList = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const loading = useAppSelector(getLoading);

  // Seed Redux from URL ?quarter=202602 on first load only
  useEffect(() => {
    const parsed = parseQuarterParam(searchParams.get('quarter'));
    if (!parsed) return;

    if (parsed.rawQuarter < 1 || parsed.rawQuarter > 4)
      setSearchParams({ quarter: `${parsed.year}04` });

    dispatch(setForecastDateLocal(parsed.date));
    dispatch(setForecastDate());
  }, [dispatch, searchParams, setSearchParams]);

  const filters = useAppSelector(selectRevenuePageFilters);
  const uniqueFilterLists = useAppSelector(getUniqueFilterLists);
  const [isDrawerOpen, setDrawerOpen] = useState<boolean>(false);
  const forecastDateLocal = useAppSelector(getForecastDateLocal);

  const effectiveForecastDate = useMemo(() => {
    const parsed = parseQuarterParam(searchParams.get('quarter'));
    return parsed ? parsed.date : forecastDateLocal;
  }, [searchParams, forecastDateLocal]);

  const handleFiltersOnClick = useCallback(() => {
    setDrawerOpen(true);
  }, [setDrawerOpen]);

  const handleFiltersOnClose = useCallback(() => {
    setDrawerOpen(false);
  }, [setDrawerOpen]);

  const selectedQuarter = useMemo(() => {
    const date = effectiveForecastDate
      ? DateTime.fromFormat(effectiveForecastDate, 'yyyy-MM-dd')
      : DateTime.now();
    const month = date.month;
    const year = date.year;
    const quarter =
      month <= 3 ? '01' : month <= 6 ? '02' : month <= 9 ? '03' : '04';
    return `${quarter} ${year}`;
  }, [effectiveForecastDate]);

  const [quarter, year] = selectedQuarter.split(' ');

  const selectedQuarterNumber = useMemo(() => {
    return parseInt(year + quarter);
  }, [year, quarter]);

  const pagePayload = useMemo(() => {
    return {
      quarter: parseInt(year + quarter),
      nextQRevenueStatus: filters.nextQRevenueStatus || undefined,
      clientGrowth: filters.clientGrowth || undefined,
      hcDeltaType: filters.hcDeltaType || undefined,
      pdmId:
        filters.PDMsToFilter?.length > 0 ? filters.PDMsToFilter : undefined,
      dmId: filters.DMsToFilter?.length > 0 ? filters.DMsToFilter : undefined,
    };
  }, [filters, quarter, year]);

  const isAnyFilterActive = useMemo(
    () =>
      !!(
        filters.nextQRevenueStatus ||
        filters.clientGrowth ||
        filters.hcDeltaType ||
        filters.PDMsToFilter?.length > 0 ||
        filters.DMsToFilter?.length > 0
      ),
    [filters],
  );

  const [isDesktop16OrSmaller, setIsDesktop16OrSmaller] = useState(
    window.innerWidth <= SCREEN_WIDTH.DESKTOP16,
  );

  useEffect(() => {
    const handleResize = throttle(() => {
      setIsDesktop16OrSmaller(window.innerWidth <= SCREEN_WIDTH.DESKTOP16);
    }, 10);

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel && handleResize.cancel(); // clean up throttle if available
    };
  }, []);

  useEffect(() => {
    dispatch(getQuarterlyRevenueAccount(pagePayload));
  }, [dispatch, pagePayload]);

  return (
    <PageWrapper
      isLoading={loading}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
      }}
    >
      <QuarterlyRevenueFilters
        isOpen={isDrawerOpen}
        isActive={isAnyFilterActive}
        onClose={handleFiltersOnClose}
        filters={filters}
        uniqueFilterLists={uniqueFilterLists}
      />

      <HeaderComp
        handleFiltersOnClick={handleFiltersOnClick}
        forecastDateLocal={effectiveForecastDate}
        selectedQuarterNumber={selectedQuarterNumber}
        isAnyFilterActive={!!isAnyFilterActive}
        onReset={() => dispatch(resetRevenuePageFilters())}
      />
      <Box
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <TableComp
          selectedQuarter={selectedQuarter}
          isDesktop16OrSmaller={isDesktop16OrSmaller}
        />
      </Box>
    </PageWrapper>
  );
};

interface QuarterlyHeaderProps {
  handleFiltersOnClick: () => void;
  forecastDateLocal?: string | null;
  selectedQuarterNumber?: number;
  isAnyFilterActive: boolean;
  onReset: () => void;
}

const HeaderComp = ({
  handleFiltersOnClick,
  forecastDateLocal,
  selectedQuarterNumber,
  isAnyFilterActive,
  onReset,
}: QuarterlyHeaderProps) => {
  const dispatch = useAppDispatch();

  const forecastDateActions: DateFilterActions = {
    type: 'dispatch',
    func: (value: string | null) => dispatch(setForecastDateLocal(value)),
    setGlobalFunc: () => dispatch(setForecastDate()),
    clean: () => dispatch(cleanForecastDate()),
  };

  return (
    <Stack>
      <Box
        sx={(theme) => ({
          display: 'flex',
          height: theme.spacing(8),
          padding: theme.spacing(1, 1),
          borderRadius: theme.spacing(1, 1, 0, 0),
          alignItems: 'center',
        })}
      >
        <Box display="flex" gap="24px">
          <Typography variant="h2">Revenue status</Typography>
          <Button
            href="https://docs.google.com/document/d/1hetB4EolOYiC5TCnHCGp3BkOjuUAeOGErf6yIiRFSbU/edit?tab=t.0#heading=h.ojr2ld2me0p9"
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            <MenuBookOutlinedIcon sx={{ marginRight: 1 }} />
            Forecast guidelines ↗
          </Button>
        </Box>
        <Box display="flex" alignItems="center" ml="auto">
          <QuarterlyCsvDownloadButton
            data-testid="quarterly-export"
            quarter={selectedQuarterNumber}
          />
          <Button
            sx={{
              mr: '8px',
              ...(!isAnyFilterActive && { color: 'text.primary' }),
            }}
            variant={isAnyFilterActive ? 'contained' : 'text'}
            onClick={handleFiltersOnClick}
            size="small"
            {...(isAnyFilterActive && { color: 'secondary' })}
          >
            <FilterListIcon />
            &nbsp; Filters
            <ChevronRightIcon />
          </Button>
          <Button
            variant="text"
            onClick={onReset}
            size="small"
            sx={{
              mr: '8px',
              backgroundColor: '#9c27b020',
              ...(!isAnyFilterActive && {
                display: 'none',
                visibility: 'hidden',
              }),
            }}
            {...(isAnyFilterActive && { color: 'secondary' })}
          >
            <RemoveCircleOutlineIcon />
            &nbsp; Reset
          </Button>
          <Box sx={{ mr: '8px' }}>
            <QuarterPicker
              dateFilterValueLocal={forecastDateLocal ?? null}
              setDateFilter={forecastDateActions}
              isQuarterlyForecast={true}
              buttonColor={
                forecastDateLocal &&
                DateTime.fromISO(forecastDateLocal).year ===
                  DateTime.local().year &&
                DateTime.fromISO(forecastDateLocal).quarter ===
                  DateTime.local().quarter
                  ? 'primary'
                  : 'secondary'
              }
            />
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};

// Inlined QuarterlyRevenueFilters component
interface QuarterlyRevenueFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  filters: QuarterlyRevenueFilter;
  isActive: boolean;
  uniqueFilterLists: {
    pdms: { value: string; label: string }[];
    dms: { value: string; label: string }[];
  };
}

const ALL_AT_ONCE = 'All at once';

const QuarterlyRevenueFilters = ({
  isOpen,
  onClose,
  filters,
  isActive,
  uniqueFilterLists,
}: QuarterlyRevenueFiltersProps) => {
  const dispatch = useAppDispatch();

  const handleReset = useCallback(() => {
    dispatch(resetRevenuePageFilters());
  }, [dispatch]);

  const handleSelectChange =
    (field: keyof QuarterlyRevenueFilter) =>
    (event: SelectChangeEvent<string>) => {
      const value =
        event.target.value === ALL_AT_ONCE ? '' : event.target.value;
      dispatch(setRevenuePageFilters({ [field]: value }));
    };

  const onChangeManagerFilterValue = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const { name, value } = event.target;

      dispatch(
        setRevenuePageFilters({
          [name]: typeof value === 'string' ? value.split(',') : value,
        }),
      );
    },
    [dispatch],
  );

  const onDeleteManagerFilterValue = useCallback(
    (value: string, name: 'PDMsToFilter' | 'DMsToFilter') => {
      dispatch(
        setRevenuePageFilters({
          [name]: filters[name].filter((x) => x !== value),
        }),
      );
    },
    [dispatch, filters],
  );

  const headCountOptions = useMemo(
    () => [
      { value: 'positive', label: 'Positive' },
      { value: 'negative', label: 'Negative' },
    ],
    [],
  );

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      keepMounted
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          minWidth: 388,
          maxWidth: 400,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        Filters
        {isActive && (
          <Button variant="text" onClick={handleReset}>
            <RemoveCircleOutlineIcon
              sx={{ color: (theme) => theme.palette.primary.main }}
            />
            &nbsp;Reset all
          </Button>
        )}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{ position: 'absolute', right: 8, top: 8 }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="quarterly-pdm-filter-label">PDM</InputLabel>
            <Select
              labelId="quarterly-pdm-filter-label"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      onDelete={() =>
                        onDeleteManagerFilterValue(value, 'PDMsToFilter')
                      }
                      onMouseDown={(event) => event.stopPropagation()}
                      label={
                        uniqueFilterLists.pdms.find((x) => x.value === value)
                          ?.label || 'Name not found'
                      }
                    />
                  ))}
                </Box>
              )}
              value={filters.PDMsToFilter}
              label="PDM"
              onChange={onChangeManagerFilterValue}
              name="PDMsToFilter"
            >
              {uniqueFilterLists.pdms.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="quarterly-dm-filter-label">DM</InputLabel>
            <Select
              labelId="quarterly-dm-filter-label"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      onDelete={() =>
                        onDeleteManagerFilterValue(value, 'DMsToFilter')
                      }
                      onMouseDown={(event) => event.stopPropagation()}
                      label={
                        uniqueFilterLists.dms.find((x) => x.value === value)
                          ?.label || 'Name not found'
                      }
                    />
                  ))}
                </Box>
              )}
              value={filters.DMsToFilter}
              label="DM"
              onChange={onChangeManagerFilterValue}
              name="DMsToFilter"
            >
              {uniqueFilterLists.dms.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Next Q revenue status</InputLabel>
            <Select
              value={filters.nextQRevenueStatus || ALL_AT_ONCE}
              label="Next Q revenue status"
              onChange={handleSelectChange('nextQRevenueStatus')}
            >
              {Object.entries(agileEngineRevenueLabels).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
              <Divider component={'li'} />
              <MenuItem value={ALL_AT_ONCE}>{ALL_AT_ONCE}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Client growth indicator</InputLabel>
            <Select
              value={filters.clientGrowth || ALL_AT_ONCE}
              label="Client growth indicator"
              onChange={handleSelectChange('clientGrowth')}
            >
              {Object.entries(clientGrowthLabels).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
              <Divider component={'li'} />
              <MenuItem value={ALL_AT_ONCE}>{ALL_AT_ONCE}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Headcount delta</InputLabel>
            <Select
              value={filters.hcDeltaType || ALL_AT_ONCE}
              label="Headcount delta"
              onChange={handleSelectChange('hcDeltaType')}
            >
              {headCountOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
              <Divider component={'li'} />
              <MenuItem value={ALL_AT_ONCE}>{ALL_AT_ONCE}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Drawer>
  );
};

// Inlined QuarterlyRevenueTable component
interface QuarterlyRevenueTableProps {
  selectedQuarter: string;
  isDesktop16OrSmaller: boolean;
}

interface CustomTableCellProps {
  sx?: SxProps<Theme> | undefined;
  customProps?: TableCellProps;
}

interface QuarterlyRevenueListTableHeaderProps {
  dispatch: ReturnType<typeof useAppDispatch>;
  isDesktop16OrSmaller: boolean;
}

const QuarterlyRevenueListTableHeader: React.FC<
  QuarterlyRevenueListTableHeaderProps
> = ({ dispatch, isDesktop16OrSmaller }) => {
  const sortBy = useAppSelector(getSortBy);
  const sortOrder = useAppSelector(getSortOrder);

  const handleSort = useCallback(
    (column: string) => {
      const sortMap: Record<string, SortAccountsBy> = {
        Accounts: SortAccountsBy.AccountName,
        RevenueStatus: SortAccountsBy.RevenueStatus,
        ClientGrowth: SortAccountsBy.ClientGrowth,
        Headcount: SortAccountsBy.Headcount,
        AeRevenueDecreaseReasons: SortAccountsBy.AeRevenueDecreaseReasons,
        PDM: SortAccountsBy.PDM,
        DM: SortAccountsBy.DM,
        PDMAndDM: SortAccountsBy.PDMAndDM,
      };
      const newSortBy = sortMap[column];
      if (!newSortBy) return;

      const newSortOrder =
        newSortBy === sortBy ? 1 - sortOrder : SortOrder.Ascending;
      dispatch(setSortBy({ sortBy: newSortBy, sortOrder: newSortOrder }));
    },
    [dispatch, sortOrder, sortBy],
  );

  return (
    <TableHead>
      <TableRow
        sx={{
          '& .MuiTableCell-root': {
            padding: '12px 16px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortAccountsBy.AccountName
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortAccountsBy.AccountName}
            onClick={() => handleSort('Accounts')}
          >
            <Typography variant="body2" fontWeight={500}>
              Accounts
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortAccountsBy.RevenueStatus
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortAccountsBy.RevenueStatus}
            onClick={() => handleSort('RevenueStatus')}
          >
            <Typography variant="body2" fontWeight={500} whiteSpace={'nowrap'}>
              Next Q<br /> revenue status
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortAccountsBy.ClientGrowth
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortAccountsBy.ClientGrowth}
            onClick={() => handleSort('ClientGrowth')}
          >
            <Typography variant="body2" fontWeight={500} whiteSpace={'nowrap'}>
              Client <br /> growth indicator
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            Expected
          </Typography>
        </TableCell>
        <TableCell>
          <TableSortLabel
            direction={
              sortBy === SortAccountsBy.AeRevenueDecreaseReasons
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortAccountsBy.AeRevenueDecreaseReasons}
            onClick={() => handleSort('AeRevenueDecreaseReasons')}
          >
            <Typography variant="body2" fontWeight={500}>
              AgileEngine <br />
              revenue decrease reasons
            </Typography>
          </TableSortLabel>
        </TableCell>
        {isDesktop16OrSmaller ? (
          <TableCell align="left">
            <TableSortLabel
              direction={
                sortBy === SortAccountsBy.PDMAndDM
                  ? sortOrder === SortOrder.Ascending
                    ? 'asc'
                    : 'desc'
                  : 'asc'
              }
              active={sortBy === SortAccountsBy.PDMAndDM}
              onClick={() => handleSort('PDMAndDM')}
            >
              <Typography whiteSpace="nowrap" variant="body2" fontWeight={500}>
                PDM / DM
              </Typography>
            </TableSortLabel>
          </TableCell>
        ) : (
          <>
            <TableCell align="left">
              <TableSortLabel
                direction={
                  sortBy === SortAccountsBy.PDM
                    ? sortOrder === SortOrder.Ascending
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                active={sortBy === SortAccountsBy.PDM}
                onClick={() => handleSort('PDM')}
              >
                <Typography
                  whiteSpace="nowrap"
                  variant="body2"
                  fontWeight={500}
                >
                  PDM
                </Typography>
              </TableSortLabel>
            </TableCell>
            <TableCell align="left">
              <TableSortLabel
                direction={
                  sortBy === SortAccountsBy.DM
                    ? sortOrder === SortOrder.Ascending
                      ? 'asc'
                      : 'desc'
                    : 'asc'
                }
                active={sortBy === SortAccountsBy.DM}
                onClick={() => handleSort('DM')}
              >
                <Typography
                  whiteSpace="nowrap"
                  variant="body2"
                  fontWeight={500}
                >
                  DM
                </Typography>
              </TableSortLabel>
            </TableCell>
          </>
        )}
        <TableCell align="right" sx={{ paddingInlineEnd: '0 !important' }}>
          <TableSortLabel
            direction={
              sortBy === SortAccountsBy.Headcount
                ? sortOrder === SortOrder.Ascending
                  ? 'asc'
                  : 'desc'
                : 'asc'
            }
            active={sortBy === SortAccountsBy.Headcount}
            onClick={() => handleSort('Headcount')}
          >
            <Typography whiteSpace="nowrap" variant="body2" fontWeight={500}>
              Headcount
              <br /> {'(delta)'}
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell align="right">
          <Typography
            whiteSpace="nowrap"
            variant="body2"
            fontWeight={500}
          ></Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

interface CustomTableCellProps {
  sx?: SxProps<Theme> | undefined;
  customProps?: TableCellProps;
}

const CustomTableCell: React.FC<
  React.PropsWithChildren<CustomTableCellProps>
> = ({ children, sx, customProps }) => {
  return (
    <TableCell
      sx={{
        padding: '8px 16px',
        ...sx,
      }}
      {...customProps}
    >
      {children}
    </TableCell>
  );
};

// Add AccountWithoutStatusRow component
const AccountWithoutStatusRow: React.FC<{
  row: {
    accountId: string;
    accountName: string;
    pdmFullName?: string | null;
    dmFullName?: string | null;
  };
  onAddRevenueSummary: (id: string) => void;
  isDesktop16OrSmaller: boolean;
}> = ({ row, onAddRevenueSummary, isDesktop16OrSmaller }) => (
  <TableRow
    sx={{
      height: '48px',
      backgroundColor: 'highlight.neutral',
      cursor: 'pointer',
      ':hover': { backgroundColor: 'action.hover' },
    }}
    onClick={() => {
      onAddRevenueSummary(row.accountId);
    }}
  >
    <CustomTableCell sx={{ pl: 1.3, maxWidth: '200px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Tooltip title="Add assessment" placement="top">
          <IconButton size="small" sx={{ color: 'primary.main' }}>
            <AddCommentIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={row.accountName} placement="right-end">
          <Typography
            whiteSpace="nowrap"
            variant="h6"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.accountName}
          </Typography>
        </Tooltip>
      </Box>
    </CustomTableCell>
    <TableCell colSpan={4} sx={{ padding: '8px 16px' }}>
      <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
        <Typography variant="body2" color="text.secondary">
          Add revenue summary
        </Typography>
      </Box>
    </TableCell>
    {isDesktop16OrSmaller ? (
      <CustomTableCell>
        <Typography whiteSpace="nowrap" variant="body1">
          {row.pdmFullName ? (
            <Tooltip title={row.pdmFullName} placement="top">
              <span>{row.pdmFullName}</span>
            </Tooltip>
          ) : (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              Not assigned
            </Box>
          )}
          {' / '}
          {row.dmFullName ? (
            <Tooltip title={row.dmFullName} placement="top">
              <span>{row.dmFullName}</span>
            </Tooltip>
          ) : (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              Not assigned
            </Box>
          )}
        </Typography>
      </CustomTableCell>
    ) : (
      <>
        <CustomTableCell>
          {row.pdmFullName ? (
            <Tooltip title={row.pdmFullName || ''} placement="top">
              <Typography
                whiteSpace="nowrap"
                variant="body1"
                textOverflow={'ellipsis'}
              >
                {row.pdmFullName}
              </Typography>
            </Tooltip>
          ) : (
            <Typography whiteSpace="nowrap" variant="body2">
              Not assigned
            </Typography>
          )}
        </CustomTableCell>
        <CustomTableCell>
          {row.dmFullName ? (
            <Tooltip title={row.dmFullName || ''} placement="top">
              <Typography
                whiteSpace="nowrap"
                variant="body1"
                textOverflow={'ellipsis'}
              >
                {row.dmFullName}
              </Typography>
            </Tooltip>
          ) : (
            <Typography whiteSpace="nowrap" variant="body2">
              Not assigned
            </Typography>
          )}
        </CustomTableCell>
      </>
    )}
    <TableCell></TableCell>
    <TableCell></TableCell>
  </TableRow>
);

// Add AccountWithStatusRow component
const AccountWithStatusRow: React.FC<{
  row: {
    accountId: string;
    accountName: string;
    headCount: number;
    statuses: Array<QuarterlyRevenueAccountSummary>;
    pdmFullName?: string | null;
    dmFullName?: string | null;
  };
  onEditRevenueSummary: (data: QuarterlyRevenueAccountSummary) => void;
  onViewRevenueSummary: (data: QuarterlyRevenueAccountSummary) => void;
  isDesktop16OrSmaller: boolean;
}> = ({
  row,
  onViewRevenueSummary,
  onEditRevenueSummary,
  isDesktop16OrSmaller,
}) => (
  <>
    {row.statuses.map((data) => (
      <React.Fragment key={`${row.accountId}-${data.id || Math.random()}`}>
        <TableRow
          sx={{
            cursor: 'pointer',
            transition: 'background 0.25s',
            '&:hover': {
              background: (theme) => theme.palette.action.hover,
            },
            verticalAlign: 'baseline',
          }}
          onClick={() => onViewRevenueSummary(row.statuses[0])}
          hover
        >
          <CustomTableCell
            sx={{
              paddingLeft: 2,
              maxWidth: '200px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 2.3,
              }}
            >
              <DoneRoundedIcon
                sx={{
                  color: 'text.secondary',
                  fontSize: '16px',
                  alignSelf: 'anchor-center',
                }}
              />
              <Tooltip title={row.accountName} placement="right-end">
                <Typography
                  whiteSpace="nowrap"
                  variant="h6"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {row.accountName}
                </Typography>
              </Tooltip>
            </Box>
          </CustomTableCell>
          <CustomTableCell>
            <RevenueStatusChip
              value={data.agileEngineRevenue}
              label={
                agileEngineRevenueLabels[
                  data.agileEngineRevenue as keyof typeof agileEngineRevenueLabels
                ]
              }
              type="agileEngineRevenue"
            />
          </CustomTableCell>
          <CustomTableCell>
            <RevenueStatusChip
              value={data.clientGrowth}
              label={
                clientGrowthLabels[
                  data.clientGrowth as keyof typeof clientGrowthLabels
                ]
              }
              type="clientGrowth"
            />
          </CustomTableCell>
          <CustomTableCell>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="left"
              whiteSpace="nowrap"
            >
              {data.growthSegmentationValue &&
                data.growthSegmentationValue + '%'}
              {data.growthSegmentation ? (
                <Chip
                  label={
                    GrowthSegmentationLabels[
                      data.growthSegmentation as keyof typeof GrowthSegmentationLabels
                    ]
                  }
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.primary',
                    marginLeft: data.growthSegmentationValue ? 0.5 : 0,
                  }}
                />
              ) : (
                <Box
                  component="span"
                  sx={{
                    color: 'text.disabled',
                    mx: 2,
                    pr: 2.5,
                  }}
                >
                  &mdash;
                </Box>
              )}
            </Typography>
          </CustomTableCell>
          <CustomTableCell sx={{ maxWidth: '295px' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {Array.isArray(data.aeRevenueDecreaseReasons) &&
                data.aeRevenueDecreaseReasons.map(
                  (item: string, index: number) => (
                    <Chip
                      key={index}
                      label={item}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        color: 'text.primary',
                      }}
                    />
                  ),
                )}
            </Box>
          </CustomTableCell>
          {isDesktop16OrSmaller ? (
            <CustomTableCell>
              <Typography whiteSpace="nowrap" variant="body1">
                {row.pdmFullName ? (
                  <Tooltip title={row.pdmFullName} placement="top">
                    <span>{row.pdmFullName}</span>
                  </Tooltip>
                ) : (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    Not assigned
                  </Box>
                )}
                {' / '}
                {row.dmFullName ? (
                  <Tooltip title={row.dmFullName} placement="top">
                    <span>{row.dmFullName}</span>
                  </Tooltip>
                ) : (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    Not assigned
                  </Box>
                )}
              </Typography>
            </CustomTableCell>
          ) : (
            <>
              <CustomTableCell>
                {row.pdmFullName ? (
                  <Tooltip title={row.pdmFullName || ''} placement="top">
                    <Typography
                      whiteSpace="nowrap"
                      variant="body1"
                      textOverflow={'ellipsis'}
                    >
                      {row.pdmFullName}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography whiteSpace="nowrap" variant="body2">
                    Not assigned
                  </Typography>
                )}
              </CustomTableCell>
              <CustomTableCell>
                {row.dmFullName ? (
                  <Tooltip title={row.dmFullName || ''} placement="top">
                    <Typography
                      whiteSpace="nowrap"
                      variant="body1"
                      textOverflow={'ellipsis'}
                    >
                      {row.dmFullName}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography whiteSpace="nowrap" variant="body2">
                    Not assigned
                  </Typography>
                )}
              </CustomTableCell>
            </>
          )}
          <CustomTableCell sx={{ paddingInlineEnd: '0 !important' }}>
            <Box display="flex" alignItems="right" justifyContent="right">
              {row.headCount}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ paddingInlineStart: 1 }}
              >
                {data.hcDelta !== 0 && data.hcDelta > 0
                  ? `(+${Number(data.hcDelta).toFixed(1)})`
                  : `(${Number(data.hcDelta).toFixed(1)})`}
              </Typography>
            </Box>
          </CustomTableCell>
          <CustomTableCell>
            <Tooltip title="Edit" placement="top">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEditRevenueSummary(row.statuses[0]);
                }}
              >
                <EditIcon sx={{ fontSize: '19px', color: 'primary.main' }} />
              </IconButton>
            </Tooltip>
          </CustomTableCell>
        </TableRow>
      </React.Fragment>
    ))}
  </>
);

const TableComp = ({
  selectedQuarter,
  isDesktop16OrSmaller,
}: QuarterlyRevenueTableProps) => {
  const dispatch = useAppDispatch();
  const [isQuarterlyRevenueModalOpen, setQuarterlyRevenueModalOpen] =
    useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedRevenueStatus, setSelectedRevenueStatus] = useState<
    QuarterlyRevenueAccountSummary | undefined
  >(undefined);
  const [mode, setMode] = useState<Mode>(Mode.VIEW);
  const [quarter, year] = selectedQuarter.split(' ');

  const quarterlyRevenueAccounts = useAppSelector(getSelectedAccount);

  const onAddRevenueSummary = useCallback((id: string) => {
    setMode(Mode.EDIT);
    if (id) {
      setSelectedAccount(id);
    }
    setSelectedRevenueStatus(undefined);
    setQuarterlyRevenueModalOpen((prev) => !prev);
  }, []);

  const onEditRevenueSummary = useCallback(
    (data: QuarterlyRevenueAccountSummary) => {
      setMode(Mode.EDIT);
      if (data.accountId) {
        setSelectedAccount(data.accountId);
      }
      setSelectedRevenueStatus(data);
      setQuarterlyRevenueModalOpen((prev) => !prev);
    },
    [],
  );

  const onViewRevenueSummary = useCallback(
    (data: QuarterlyRevenueAccountSummary) => {
      setMode(Mode.VIEW);
      if (data.accountId) {
        setSelectedAccount(data.accountId);
      }
      setSelectedRevenueStatus(data);
      setQuarterlyRevenueModalOpen((prev) => !prev);
    },
    [],
  );

  const accountsWithoutStatuses = useMemo(
    () => quarterlyRevenueAccounts.filter((row) => !row.statuses.length),
    [quarterlyRevenueAccounts],
  );

  const accountsWithStatuses = useMemo(
    () => quarterlyRevenueAccounts.filter((row) => row.statuses.length),
    [quarterlyRevenueAccounts],
  );

  return (
    <>
      <Box
        component={Paper}
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          <Table aria-label="collapsible table" stickyHeader>
            <QuarterlyRevenueListTableHeader
              dispatch={dispatch}
              isDesktop16OrSmaller={isDesktop16OrSmaller}
            />
            {quarterlyRevenueAccounts.length > 0 ? (
              <TableBody className="noOverrides">
                {/* Render accounts without statuses */}
                {accountsWithoutStatuses.map((row, i) => (
                  <AccountWithoutStatusRow
                    key={i}
                    row={row}
                    onAddRevenueSummary={onAddRevenueSummary}
                    isDesktop16OrSmaller={isDesktop16OrSmaller}
                  />
                ))}
                {/* Render accounts with statuses */}
                {accountsWithStatuses.map((row) => (
                  <AccountWithStatusRow
                    key={row.accountId}
                    row={row}
                    onViewRevenueSummary={onViewRevenueSummary}
                    onEditRevenueSummary={onEditRevenueSummary}
                    isDesktop16OrSmaller={isDesktop16OrSmaller}
                  />
                ))}
              </TableBody>
            ) : (
              <TableCell colSpan={13}>
                <TableBody
                  sx={{
                    display: 'flex',
                    alignContent: 'center',
                    justifyContent: 'center',
                    marginTop: '3%',
                    marginBottom: '3%',
                    fontSize: '1rem',
                    color: 'grey',
                  }}
                >
                  {'No data available'}
                </TableBody>
              </TableCell>
            )}
          </Table>
        </TableContainer>
      </Box>
      <ForecastModal
        isModalOpen={isQuarterlyRevenueModalOpen}
        handleCloseModal={() => setQuarterlyRevenueModalOpen(false)}
        selectedAccount={selectedAccount}
        selectedQuarter={year + '' + quarter}
        data={selectedRevenueStatus}
        mode={mode}
        setMode={setMode}
      />
    </>
  );
};

interface QuarterlyCsvDownloadButtonProps {
  style?: 'default' | 'icon';
  quarter?: number;
}

export const QuarterlyCsvDownloadButton = ({
  style = 'default',
  quarter,
}: QuarterlyCsvDownloadButtonProps) => {
  const quarterlyRevenueAccounts = useAppSelector(getSelectedAccount);

  const accountsWithoutStatuses = useMemo(
    () => quarterlyRevenueAccounts.filter((row) => !row.statuses.length),
    [quarterlyRevenueAccounts],
  );

  const accountsWithStatuses = useMemo(
    () => quarterlyRevenueAccounts.filter((row) => row.statuses.length),
    [quarterlyRevenueAccounts],
  );

  const handleQuarterlyExport = useCallback(async () => {
    const result = [...accountsWithoutStatuses, ...accountsWithStatuses];

    if (!result || result.length === 0) return;

    const csvData = result.map((account) => {
      const revenueStatus = account.statuses.find(
        (status) => status.quarter === quarter,
      );
      const currentQ = Number(quarter ? quarter % 100 : DateTime.now().quarter);

      return {
        Account: account.accountName || '',
        Quarter: quarter ?? '',
        'AE revenue status': revenueStatus?.agileEngineRevenue || '',
        'Revenue decrease reasons':
          revenueStatus?.aeRevenueDecreaseReasons || '',

        'Forecasted revenue': revenueStatus?.expectedRevenue || '',
        [`Q${currentQ === 1 ? 4 : currentQ - 1} Actual revenue`]:
          revenueStatus?.previousQuarterRevenue ??
          (account?.previousQuarterRevenue || ''),

        'Client growth indicator': revenueStatus?.clientGrowth || '',
        'Growth segmentation': revenueStatus?.growthSegmentation || '',
        'Growth segmentation value':
          revenueStatus?.growthSegmentationValue || '',

        'HC delta': revenueStatus?.hcDelta || '',
        'Head count': account?.headCount || '',

        'Affected people': revenueStatus?.affectedPeople || '',

        Locations: revenueStatus?.locations || '',
        Seniorities: revenueStatus?.seniorities || '',
        'Tech flows': revenueStatus?.techFlows || '',

        Overview: revenueStatus?.overview || '',
        Forecast: revenueStatus?.forecast || '',
        'Growth actions': revenueStatus?.growthActions || '',

        PDM: account.pdmFullName || '',
        DM: account.dmFullName || '',
      };
    });

    const csv = Papaparse.unparse(csvData, { quotes: true });
    downloadCsv(csv, 'quarterly-revenue-summary.csv');
  }, [accountsWithoutStatuses, accountsWithStatuses, quarter]);

  if (style === 'icon') {
    return (
      <IconButton onClick={handleQuarterlyExport}>
        <FileDownloadIcon sx={{ color: '#00000' }} />
      </IconButton>
    );
  }

  return (
    <Button size="small" sx={{ mx: 2 }} onClick={handleQuarterlyExport}>
      <GridOnIcon
        sx={{
          color: '#000000',
        }}
      />
      <Typography variant="body1" color="text.primary" sx={{ marginLeft: 0.7 }}>
        Export
      </Typography>
    </Button>
  );
};
